import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export default function CEOChat({ compact = false }) {
  const [history, setHistory] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const boxRef = useRef(null);
  const agent = AGENTS.ceo;

  useEffect(() => {
    soloApi.chatHistory("ceo").then(({ data }) => setHistory(data));
  }, []);

  useEffect(() => {
    boxRef.current?.scrollTo({ top: boxRef.current.scrollHeight, behavior: "smooth" });
  }, [history, sending]);

  const send = async () => {
    if (!text.trim() || sending) return;
    const msg = text.trim();
    setText("");
    setSending(true);
    setHistory((h) => [...h, { user_message: msg, assistant_message: null, created_at: new Date().toISOString() }]);
    try {
      const { data } = await soloApi.chatSend(msg, "ceo");
      setHistory((h) => {
        const copy = [...h];
        copy[copy.length - 1] = { ...copy[copy.length - 1], assistant_message: data.reply };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={"glass rounded-3xl flex flex-col " + (compact ? "h-[520px]" : "h-[calc(100vh-120px)]")}>
      <div className="flex items-center gap-3 p-5 border-b border-white/10">
        <div className="w-11 h-11 slime-blob overflow-hidden" style={{ background: agent.gradient }}>
          <img src={agent.avatar} alt="Casey" className="w-full h-full object-cover mix-blend-luminosity" />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-neon-chrome">Casey · CEO co-founder</div>
          <div className="font-display text-xl font-bold chrome-text">Always on. Knows everything.</div>
        </div>
        <Sparkles className="ml-auto text-neon-cyan animate-pulse" />
      </div>

      <div ref={boxRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="ceo-chat-history">
        {history.length === 0 && (
          <div className="text-center text-white/60 mt-16">
            <div className="font-display text-2xl mb-2 chrome-text">Say hi to Casey</div>
            <p className="max-w-sm mx-auto text-sm">Ask about your idea, positioning, next best action, or anything at all. This is your home base.</p>
          </div>
        )}
        {history.map((m) => (
          <div key={m.created_at + "-" + (m.user_message || "")} className="space-y-3">
            <motion.div initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} className="flex justify-end">
              <div className="bg-neon-pink/90 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[80%] font-medium">
                {m.user_message}
              </div>
            </motion.div>
            {m.assistant_message !== null && (
              <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="flex justify-start">
                <div className="bg-white/10 text-white rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[80%] whitespace-pre-wrap border border-white/15">
                  {m.assistant_message}
                </div>
              </motion.div>
            )}
            {m.assistant_message === null && sending && (
              <div className="flex gap-1 pl-2">
                <span className="w-2 h-2 bg-white/60 rounded-full animate-pulse" />
                <span className="w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                <span className="w-2 h-2 bg-white/60 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="p-4 border-t border-white/10 flex gap-2">
        <Textarea
          data-testid="ceo-chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask Casey anything…"
          className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-2xl min-h-[52px] max-h-32 resize-none"
        />
        <Button data-testid="ceo-chat-send" onClick={send} disabled={!text.trim() || sending} className="rounded-2xl bg-white text-slate-900 hover:bg-neon-cyan font-bold h-[52px] px-5">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
